import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { X, ArrowRight, ChevronDown } from 'lucide-react';
import { JOURNEYS } from '../../data/journeyConfig';
import { WORKSPACE_TOOLS, toolIconUrl } from '../../data/workspaceTools';
import { LIFECYCLE_PILLARS } from '../../data/lifecyclePillarSEO';
import { localizeStage } from '../../data/lifecycleStages';
import { useUserState } from '../../lib/userState';
import { openMatcher } from '../../utils/openMatcher';
import {
  BUILDERS_MATCHER,
  SUPPLIERS_MATCHER,
  TRADES_MATCHER,
  PROS_MATCHER,
} from '../../data/megaMenuMatchers';

/**
 * LifecycleStageModal
 *
 * One popup component, driven by the `stage` object from
 * /data/lifecycleStages.js. Renders:
 *   1. Stage heading + intro
 *   2. Project tiles (existing JourneyChips logic) - clicking one
 *      closes this modal and asks the parent to open the canonical
 *      JourneyModal for that journey, preserving the existing flow.
 *   3. Match flows (each opens the canonical mega-menu matcher).
 *   4. Per-section content links + workspace tool tiles.
 *   5. Soft CTA to /one-on-one-support.
 *
 * Props:
 *   stage             - object from LIFECYCLE_STAGES
 *   onClose()         - close the modal
 *   onSelectJourney(journey)  - parent opens the existing JourneyModal
 */
const MATCHER_LABEL_TO_PAYLOAD = {
  'Shortlist a builder': BUILDERS_MATCHER,
  'Shortlist a tradie': TRADES_MATCHER,
  'Shortlist a supplier': SUPPLIERS_MATCHER,
  'Shortlist a professional': PROS_MATCHER,
  'Find a tradie for finishing jobs': TRADES_MATCHER,
  'Find a tradie for repairs': TRADES_MATCHER,
  'Find a supplier for late selections': SUPPLIERS_MATCHER,
  'Find an independent inspector': PROS_MATCHER,
  'Find an inspector': PROS_MATCHER,
  'Find a property lawyer': PROS_MATCHER,
  'Find a conveyancer': PROS_MATCHER,
  'Find a finance broker': PROS_MATCHER,
};

const LifecycleStageModal = ({ stage: rawStage, onClose, onSelectJourney }) => {
  const stateCode = useUserState();
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Accordion: which content section is expanded. Single-open keeps
  // the default view a short, clean list of topics. Reset to the first
  // section whenever the stage changes.
  const [openIdx, setOpenIdx] = useState(0);
  useEffect(() => { setOpenIdx(0); }, [rawStage?.slug]);

  if (!rawStage) return null;

  // Resolve SA/VIC/waitlist content for the visitor's picked state.
  const stage = localizeStage(rawStage, stateCode);

  // Map project-tile slugs -> full journey objects for the visual tiles
  const journeys = stage.projectTiles
    .map((slug) => JOURNEYS.find((j) => j.slug === slug))
    .filter(Boolean);

  // Map workspace tool ids -> full tool objects
  const toolById = (id) => WORKSPACE_TOOLS.find((t) => t.id === id);

  // Trigger a matcher entry. Supports both the canonical
  // mega-menu payloads (BUILDERS / TRADES / etc.) and the dedicated
  // events used for H&L and floor plans.
  const fireMatcher = (m) => {
    onClose();
    if (m.event && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(m.event));
      return;
    }
    const payload = MATCHER_LABEL_TO_PAYLOAD[m.label];
    if (payload) {
      openMatcher({ ...payload, sourcePlacement: m.sourcePlacement || `lifecycle:${stage.slug}` });
    }
  };

  // Build full list of matcher pills - merges the optional .matcher
  // single + .matchers array forms used inside lifecycleStages.js.
  const matchers = [
    ...(stage.matcher ? [stage.matcher] : []),
    ...(stage.matchers || []),
  ];

  const node = (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lifecycle-modal-heading"
      data-testid="lifecycle-stage-modal"
    >
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} />

      <div className="relative bg-white border border-gray-200 w-full sm:max-w-xl max-h-[92vh] sm:max-h-[84vh] sm:rounded-3xl rounded-t-2xl shadow-2xl overflow-y-auto overscroll-contain animate-in fade-in slide-in-from-bottom-6 sm:zoom-in-95 duration-300">
        {/* Persistent close button - stays pinned to the top-right of
            the modal viewport while the content scrolls, so users
            always have an exit no matter how far they scroll. */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="sticky top-3 float-right z-20 mr-3 flex items-center justify-center h-8 w-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
          data-testid="lifecycle-modal-close"
        >
          <X className="w-4 h-4" strokeWidth={2.25} />
        </button>

        {/* Header - inline block, scrolls with content */}
        <div className="px-5 sm:px-7 pt-4 sm:pt-5 pb-3 pr-12 sm:pr-14 border-b border-gray-100">
          <div className="flex items-start gap-3 sm:gap-4">
            {stage.heroImage && (
              <img
                src={stage.heroImage}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className="flex-shrink-0 w-14 h-14 sm:w-[72px] sm:h-[72px] rounded-full object-cover ring-1 ring-gray-200 shadow-sm mt-0.5"
              />
            )}
            <div className="min-w-0">
            <p className="text-[10.5px] sm:text-[11px] font-semibold uppercase tracking-[0.07em] text-[#1d8efe]">
              {stage.name}
            </p>
            <h2
              id="lifecycle-modal-heading"
              className="text-[19px] sm:text-[22px] font-bold text-[#1d1d1f] leading-tight mt-1"
              data-testid="lifecycle-modal-heading"
            >
              {stage.heading}
            </h2>
            <p className="text-[13px] sm:text-[14px] text-[#5a5a5f] mt-1 leading-snug max-w-2xl">
              {stage.intro}
            </p>
            {stage.emotion && (
              <p className="text-[12.5px] sm:text-[13px] text-[#6a6a6f] mt-1.5 leading-snug max-w-2xl">
                {stage.emotion}
              </p>
            )}
            {stage.ageWhisper && (
              <p className="text-[11px] sm:text-[11.5px] text-[#9a9aa0] mt-1.5">
                {stage.ageWhisper}
              </p>
            )}
            {LIFECYCLE_PILLARS[stage.slug] && (
              <Link
                to={`/lifecycle/${LIFECYCLE_PILLARS[stage.slug].urlSlug}`}
                onClick={onClose}
                className="inline-flex items-center gap-1 text-[12.5px] sm:text-[13px] font-semibold text-[#1d8efe] hover:text-[#1670cc] mt-2.5 underline-offset-2 hover:underline"
                data-testid="lifecycle-modal-pillar-link"
              >
                Read the full guide
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
            </div>
          </div>
        </div>

        {/* Body - two-column masonry on larger screens so the panel
            uses its width and stays short (less scrolling). Each
            section is kept whole with break-inside-avoid. */}
        <div className="px-5 sm:px-7 py-4 flex flex-col gap-3">
          {/* Waitlist note - shown for states where BuildPilot isn't
              live yet; the local SA/VIC links are hidden and only
              national guides remain. */}
          {stage.waitlist && stage.hasLocalContent && (
            <div
              className="rounded-xl border border-[#1d8efe]/25 bg-[#1d8efe]/[0.06] px-4 py-3"
              data-testid="lifecycle-waitlist-note"
            >
              <p className="text-[12.5px] sm:text-[13px] text-[#1d4e8e] leading-relaxed">
                BuildPilot is live in <strong>South Australia</strong> and <strong>Victoria</strong>.
                {stage.stateName ? ` ${stage.stateName} is` : ' Your state is'} coming soon — the national
                guides below still apply.{' '}
                <Link
                  to="/waitlist"
                  onClick={onClose}
                  className="font-semibold text-[#1d8efe] hover:text-[#1670cc] underline-offset-2 hover:underline whitespace-nowrap"
                  data-testid="lifecycle-waitlist-cta"
                >
                  Join the waitlist →
                </Link>
              </p>
            </div>
          )}

          {/* Match flows - primary action, blue solid pills */}
          {matchers.length > 0 && (
            <section data-testid="lifecycle-matchers">
              <h3 className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-gray-400 mb-1.5">
                Get matched in 60 seconds
              </h3>
              <div className="flex flex-wrap gap-2">
                {matchers.map((m) => (
                  <button
                    key={m.label}
                    type="button"
                    onClick={() => fireMatcher(m)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1d8efe] text-white rounded-full text-[12.5px] font-semibold shadow-sm transition duration-200 hover:bg-[#1670cc] hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
                    data-testid={`lifecycle-matcher-${m.label.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {m.label}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Project tiles - pills with icon, outlined */}
          {journeys.length > 0 && (
            <section data-testid="lifecycle-projects">
              <h3 className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-gray-400 mb-1.5">
                What are you doing?
              </h3>
              <div className="flex flex-wrap gap-2">
                {journeys.map((j) => (
                  <button
                    key={j.slug}
                    type="button"
                    onClick={() => { onClose(); onSelectJourney && onSelectJourney(j); }}
                    className="group inline-flex items-center gap-1.5 pl-1.5 pr-3.5 py-1.5 bg-white border border-gray-200 hover:border-[#1d8efe] hover:bg-[#1d8efe]/[0.04] rounded-full transition duration-200 hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0"
                    data-testid={`lifecycle-project-${j.slug}`}
                  >
                    <img src={j.iconSrc} alt="" aria-hidden="true" className="w-6 h-6 object-contain flex-shrink-0" loading="lazy" />
                    <span className="text-[12.5px] font-semibold text-[#1d1d1f]">{j.name}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Content sections - collapsed into an accordion so the
              default view is a clean list of topics; every button is
              one tap away inside its section. */}
          <div className="flex flex-col gap-1.5" data-testid="lifecycle-sections-accordion">
          {stage.sections.map((section, idx) => {
            const count = section.links?.length || section.workspaceToolIds?.length || 0;
            const open = openIdx === idx;
            return (
            <div key={idx} className="rounded-xl border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenIdx(open ? null : idx)}
                aria-expanded={open}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                data-testid={`lifecycle-section-toggle-${idx}`}
              >
                <span className="text-[13px] font-semibold text-[#1d1d1f]">{section.title}</span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[11px] font-medium text-gray-400">{count}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {open && (
              <div className="px-4 pb-3 pt-0.5 animate-in fade-in slide-in-from-top-1 duration-200" data-testid={`lifecycle-section-${idx}`}>

              {section.workspaceToolIds && (
                <div className="flex flex-wrap gap-1.5 gap-y-2">
                  {section.workspaceToolIds.map((id) => {
                    const tool = toolById(id);
                    if (!tool) return null;
                    return (
                      <Link
                        key={tool.id}
                        to={tool.route}
                        onClick={onClose}
                        className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1 bg-[#f3eeff] hover:bg-[#e8dfff] border border-[#dcd0ff] hover:border-[#b9a3ff] rounded-full transition duration-200 hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0"
                        data-testid={`lifecycle-tool-${tool.id}`}
                      >
                        <img src={toolIconUrl(tool)} alt="" aria-hidden="true" className="w-5 h-5 object-contain flex-shrink-0" loading="lazy" />
                        <span className="text-[12px] font-semibold text-[#3b2a7a]">
                          {tool.name}
                        </span>
                        {tool.premium && (
                          <span className="text-[9px] uppercase tracking-[0.07em] font-bold text-[#3b2a7a]/70 ml-0.5">
                            Pro
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}

              {section.links && (
                <div className="flex flex-wrap gap-1.5 gap-y-2">
                  {section.links.map((l) => (
                    <Link
                      key={l.href}
                      to={l.href}
                      onClick={onClose}
                      className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 hover:border-[#1d8efe] rounded-full text-[12px] text-gray-700 hover:text-[#1d8efe] transition duration-200 hover:-translate-y-0.5 active:translate-y-0"
                      data-testid={`lifecycle-link-${l.href.replace(/[^a-z0-9]+/gi, '-').slice(0, 40)}`}
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
              </div>
              )}
            </div>
            );
          })}
          </div>
        </div>

        {/* Soft CTA footer */}
        {stage.softCta && (
          <div className="px-5 sm:px-7 py-3.5 border-t border-gray-100 bg-gradient-to-r from-[#fefaf5] to-[#fdf3ea]">
            <Link
              to={stage.softCta.href}
              onClick={onClose}
              className="flex items-center justify-between gap-3 text-[13px] font-semibold text-[#cc6a1f] hover:text-[#aa5418] transition-colors"
              data-testid="lifecycle-soft-cta"
            >
              <span>{stage.softCta.label}</span>
              <ArrowRight className="w-4 h-4 flex-shrink-0" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

export default LifecycleStageModal;
